import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { UploadImage, UploadResponse } from '../../../connection/upload-image';
import { DESTINATION_TYPE, RESOLUTION_OPTIONS } from '../../../stores/reducers/broadcasting';
import { SelectGroupOption } from '../../general-components/select-group';
import { random } from '../../../utils/random';
import * as ModalActions from '../../../stores/actions/modal';
import * as SettingsActions from '../../../stores/actions/settings';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import SelectSmall from '../../general-components/select-small';
import * as Alerts from '../../general-components/alert-service';
import WaitingIndicator from '../../general-components/waiting-indicator';
import { isValidRtmpAddress } from '../../../utils/utils';
import { GreenroomState } from '../../../stores/types/types';
import LoadingSpinner from '../../general-components/loading-spinner';
import useValidateRtmp from '../../../hooks/use-validate-rtmp';
import {IMG_TYPES} from "../../../types/constants";

interface Props {
	destinationType: SelectGroupOption;
}

export default function StreamDestinationSettings(props: Props) {
	const dispatch = useDispatch();
	const {
		destinationType,
	} = props;

	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);

	const [titleInput, setTitleInput] = useState<string>('');
	const [addressInput, setAddressInput] = useState<string>('');
	const [keyInput, setKeyInput] = useState<string>('');
	const [qualityInput, setQualityInput] = useState<string>(RESOLUTION_OPTIONS[0].value);
	const [logoInput, setLogoInput] = useState<string>('');
	const [uploadingLogo, setUploadingLogo] = useState<boolean>(false);

	const logoImageInput = useRef<HTMLInputElement | null>(null);

	const {
		isValidatingRtmpEndpoint,
		rtmpOverrideModal,
		validateRtmpEndpoint,
	} = useValidateRtmp();

	function copyInput(e: React.MouseEvent<HTMLButtonElement>, address: boolean) {
		e.preventDefault();
		navigator.clipboard.writeText(address ? addressInput : keyInput);
	}

	function showError(message: string) {
		Alerts.showAlert({
			message,
			type: 'warning',
			duration: 3000,
		});
	}

	function postRtmpValidation(updateInputAddress: string) {
		const newDestination = {
			id: random(10),
			name: destinationType?.label,
			url: updateInputAddress,
			stream_key: keyInput,
			resolution: qualityInput,
			logo: '',
			type: destinationType?.value,
		};

		if (destinationType?.value === DESTINATION_TYPE.OTHER) {
			newDestination.name = titleInput;
			newDestination.logo = logoInput;
		}

		dispatch(SettingsActions.addCustomDestination(newDestination));
		dispatch(ModalActions.setModalOpen(false));
	}

	async function submitForm(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();

		const updateInputAddress = addressInput.replace(/\s+/g, '');

		await validateRtmpEndpoint(
			destinationType?.value === DESTINATION_TYPE.OTHER
				? titleInput
				: destinationType?.label ?? '',
			updateInputAddress,
			keyInput,
			3,
			() => postRtmpValidation(updateInputAddress),
		);
	}

	function cancelForm(e: React.MouseEvent<HTMLButtonElement>) {
		e.preventDefault();
		dispatch(ModalActions.setModalOpen(false));
	}

	function pickLogoImage(e: React.MouseEvent<HTMLDivElement>) {
		e.preventDefault();
		logoImageInput.current?.click();
	}

	function logoImageChange(element: React.ChangeEvent<HTMLInputElement>) {
		element.preventDefault();
		setUploadingLogo(true);
		if (element.target.files) {
			UploadImage(token, element.target.files).then((res: UploadResponse) => {
				setUploadingLogo(false);
				setLogoInput(res.url);
			}).catch((error) => {
				console.error(error);
				setUploadingLogo(false);
				Alerts.showAlert({
					message: 'Error uploading logo',
					description: 'We ran into an issue uploading that image. Please try again.',
					duration: 4000,
				});
			});
		} else {
			setUploadingLogo(false);
		}
	}

	return (
		<div className={classNames('info-modal', { other: destinationType?.value === DESTINATION_TYPE.OTHER })}>
			<div className="info-modal-header destination margin-bottom-medium">
				{
					destinationType?.value !== DESTINATION_TYPE.OTHER
					&& <img
						src={destinationType?.description}
						alt={destinationType?.description}
					/>
				}
				<h1>{
					destinationType?.value === DESTINATION_TYPE.OTHER
						? 'Create Destination'
						: `Add to ${destinationType?.label}`
				}</h1>
			</div>
			<form onSubmit={submitForm}>
				<div className="destination-modal-body">
					{destinationType?.value === DESTINATION_TYPE.OTHER && (
						<>
							<div className="destination-modal-logo" onClick={pickLogoImage}>
								{uploadingLogo && <WaitingIndicator />}
								{!uploadingLogo && (
									!logoInput ? (
										<>
											<Icon name={ICONS.ADD} color={COLORS.WHITE} size={16} />
											Logo
										</>
									) : (<img src={logoInput} alt={logoInput} />)
								)}
							</div>
							<input
								style={{ display: 'none' }}
								type="file"
								accept={IMG_TYPES
									.map((a) => `.${a}`)
									.join(',')
								}
								ref={logoImageInput}
								onChange={logoImageChange}
							/>
						</>
					)}
					<div className={classNames('destination-inputs', { other: destinationType?.value === DESTINATION_TYPE.OTHER })}>
						{destinationType?.value === DESTINATION_TYPE.OTHER && <div className="input-container">
							<div className="input-header">
								<span>Title*</span>
							</div>
							<div className="input-body">
								<input type="text" onChange={({ target }) => setTitleInput(target.value)} value={titleInput} placeholder="Name your destination..." />
							</div>
						</div>}
						<div className="input-container">
							<div className="input-header">
								<span>RTMP address*</span>
							</div>
							<div className="input-body">
								<input type="text" onChange={({ target }) => setAddressInput(target.value)} value={addressInput} placeholder="Paste RTMP address here..." />
								<button className="copy-button" onClick={(e) => copyInput(e, true)}>
									<Icon name={ICONS.COPY_FILLED} color={COLORS.WHITE} size={10} />
								</button>
							</div>
						</div>
						<div className="input-container">
							<div className="input-header">
								<span>Stream key*</span>
							</div>
							<div className="input-body">
								<input type="text" onChange={({ target }) => setKeyInput(target.value)} value={keyInput} placeholder="Paste stream key here..." />
								<button className="copy-button" onClick={(e) => copyInput(e, false)}>
									<Icon name={ICONS.COPY_FILLED} color={COLORS.WHITE} size={10} />
								</button>
							</div>
						</div>
						<div className="input-container">
							<div className="input-header">
								<span>Video Quality</span>
							</div>
							<div className="input-body">
								<SelectSmall
									options={RESOLUTION_OPTIONS}
									onChange={setQualityInput}
									label={qualityInput}
									selected={RESOLUTION_OPTIONS.find(
										(option) => option.value === qualityInput,
									) || RESOLUTION_OPTIONS[0]}
									optionHeight={48}
								/>
							</div>
						</div>
					</div>
				</div>
				<div className="info-modal-bottom">
					<div className="info-modal-buttons">
						<button className="info-gray" onClick={(e) => cancelForm(e)} disabled={false}>
							Cancel
						</button>
						<button
							className="info-green transition-opacity"
							disabled={isValidatingRtmpEndpoint}
						>
							{isValidatingRtmpEndpoint && (
								<>
									<LoadingSpinner
										height={10}
										width={10}
										color="#09090A"
										stroke={10}
										style={{ display: 'flex', margin: 'none' }}
									/>
									&nbsp;
								</>
							)}
							Save
						</button>
					</div>
				</div>
				{rtmpOverrideModal}
			</form>
		</div>
	);
}
